// node_modules
import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { parse, format } from "date-fns";

// primereact components
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { ButtonGroup } from "primereact/buttongroup";
import { Dialog } from "primereact/dialog";
import { OrderList } from "primereact/orderlist";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

// utils
import { convertForReader } from "../../utils/reader4digits";
import sortMenuByStationOptions from "../../utils/sortMenuByStationOptions";

// styles
import * as styles from "./OrderCard.module.css";

// assets
import CheckInCircleIcon from "../../assets/check-in-circle.svg";

const OrderCard = ({
  order,
  makeOrderCompleted,
  selectedStation,
  stationOptions,
}) => {
  const orderRef = useRef(null);
  const printOrder = useReactToPrint({ contentRef: orderRef });

  const [printed, setPrinted] = React.useState(false);
  console.log("Order: ", order);

  const orderSubStationName =
    order?.sub_station_name ||
    selectedStation?.sub_stations?.find(
      (subStation) =>
        +subStation?.id ===
        +(order?.sub_station_id || order?.subStationId || -1),
    )?.name ||
    null;

  const handlePrintOrder = () => {
    if (window.electronAPI?.print) {
      const dataToPrint = [
        { type: "order-id", value: convertForReader(order.id) },
        { type: "divider" },
        { type: "text", value: selectedStation.name },
        { type: "divider" },
        { type: "text", value: `ORDER № ${order.number}` },
        ...(selectedStation?.is_multi && orderSubStationName
          ? [{ type: "text", value: `SUB-STATION: ${orderSubStationName}` }]
          : []),
        { type: "divider" },
        { type: "text-big", value: `${order.name} ${order.surname}` },
        { type: "divider" },
        { type: "text", value: order.dining_option_name },
        { type: "title", value: "ALLERGIES" },
        {
          type: "text-bold-upper",
          value: `${(order.allergies || []).reduce(
            (acc, current, index, array) => {
              if (index === array.length - 1) {
                return acc + current;
              } else {
                return acc + current + ", ";
              }
            },
            "",
          )}`,
        },
      ];

      const menuToPrint = sortMenuByStationOptions(order.menu, stationOptions);

      menuToPrint.length &&
        menuToPrint.forEach((item) => {
          dataToPrint.push({ type: "title", value: item.group_name });
          dataToPrint.push({
            type: "text",
            value: (() => {
              return item.options
                ? item.options.reduce(
                    (res, option) => res + option.option_name + ", ",
                    "",
                  )
                : "";
            })(),
          });
        });

      dataToPrint.push({ type: "title", value: "Special Notes" });
      dataToPrint.push({ type: "text-small", value: order.special_note });

      dataToPrint.push({ type: "title", value: "Order Date Time" });
      dataToPrint.push({
        type: "text",
        value: format(
          parse(order.creation_time, "yyyy-MM-dd HH:mm", new Date()),
          "yyyy-MM-dd hh:mm aa",
        ),
      });
      dataToPrint.push({ type: "divider" });
      dataToPrint.push({
        type: "text-big",
        value: `${order.name} ${order.surname}`,
      });
      dataToPrint.push({ type: "divider" });

      console.log(dataToPrint);

      window.electronAPI.print(dataToPrint);
    } else {
      printOrder();
    }
  };

  return (
    <div className={`${styles.orderCard} ${printed ? styles.printed : ""}`}>
      <div className={styles.orderDataWrapper}>
        <div className={styles.header}>
          <div>
            <div>ORDER №{order.number}</div>
            {selectedStation?.is_multi && orderSubStationName ? (
              <div
                style={{ fontSize: "13px", color: "#5b5b5b", marginTop: "4px" }}
              >
                {orderSubStationName}
              </div>
            ) : null}
          </div>
          <div>
            {format(
              parse(order.creation_time, "yyyy-MM-dd HH:mm", new Date()),
              "hh:mm aa",
            )}
          </div>
        </div>

        <div className={styles.dataWrapper}>
          {order.customer_code && (
            <div className={`${styles.row} ${styles.customerCodeRow}`}>
              <div className={styles.rowHeader}>Customer Code</div>
              <div className={styles.customerCode}>{order.customer_code}</div>
            </div>
          )}

          <div className={styles.row}>
            <div className={styles.rowHeader}>Name</div>
            <div>
              {order.name} {order.surname}
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.rowHeader}>Dining Option</div>
            <div>{order.dining_option_name}</div>
          </div>

          <div className={`${styles.row} ${styles.allergyRow}`}>
            <div className={`${styles.rowHeader} ${styles.allergyRow}`}>
              Allergies
            </div>
            <div>
              {(order.allergies || []).map((allergy) => allergy + ", ")}
            </div>
          </div>

          {(() => {
            const menuToRender = sortMenuByStationOptions(
              order.menu,
              stationOptions,
            );

            return (
              menuToRender.length &&
              menuToRender.map((item) => (
                <div className={styles.row} key={item.group_name}>
                  <div className={styles.rowHeader}>{item.group_name}</div>
                  <div>
                    {item.options &&
                      item.options.map((option) => option.option_name + ", ")}
                  </div>
                </div>
              ))
            );
          })()}

          <div className={styles.row}>
            <div className={styles.rowHeader}>Special Instructions</div>
            <div>{order.special_note}</div>
          </div>
        </div>
      </div>

      {/* Printed View */}
      <div ref={orderRef} className={styles.printedView}>
        <div>{selectedStation.name}</div>
        {selectedStation?.is_multi && orderSubStationName ? (
          <div style={{ fontSize: "13px", color: "#5b5b5b", marginTop: "4px" }}>
            {orderSubStationName}
          </div>
        ) : null}
        <div className={styles.divider}></div>
        <div>ORDER № {order.number}</div>
        <div className={styles.divider}></div>
        <div className={styles.bigFont}>
          {order.name} {order.surname}
        </div>
        <div className={styles.divider}></div>
        <div>{order.dining_option_name}</div>
        <div className={styles.divider}></div>
        <div>ALLERGIES</div>
        <div style={{ textTransform: "uppercase", fontWeight: "bold" }}>
          {(order.allergies || []).map((allergy) => allergy + ", ")}
        </div>

        <div className={styles.divider}></div>

        {(() => {
          const menuToRender = sortMenuByStationOptions(
            order.menu,
            stationOptions,
          );

          return (
            menuToRender.length &&
            menuToRender.map((item) => (
              <div key={item.group_name}>
                <div className={styles.smallFont}>{item.group_name}</div>
                <div>
                  {item.options &&
                    item.options.map((option) => option.option_name + ", ")}
                </div>

                <div className={styles.divider}></div>
              </div>
            ))
          );
        })()}
        <div className={styles.smallFont}>Special Notes</div>
        <div className={styles.smallFont}>{order.special_note}</div>
        <div className={styles.divider}></div>

        <div className={styles.extraSmallFont}>Order Date Time</div>
        <div>
          {format(
            parse(order.creation_time, "yyyy-MM-dd HH:mm", new Date()),
            "yyyy-MM-dd hh:mm aa",
          )}
        </div>
        <div className={styles.divider}></div>
        <div className={styles.bigFont}>
          {order.name} {order.surname}
        </div>
        <div className={styles.divider}></div>
      </div>

      <div className={styles.orderButtons}>
        <Button
          onClick={(e) => {
            setPrinted(true);
            handlePrintOrder();
          }}
          onKeyDown={(e) => {
            e.preventDefault();
          }}
        >
          <i className="pi pi-print" />
          {printed ? "Print Again" : "Print"}
        </Button>

        <Button onClick={() => makeOrderCompleted({ id: order.id })}>
          <img width={28} src={CheckInCircleIcon} alt="" />
          Done
        </Button>
      </div>
    </div>
  );
};

export default OrderCard;
